/**
 * Region resolution for a single, shared deployment.
 *
 * WHY: one deployment serves IN and US. The active region comes from the `region`
 * query param the mobile app already appends to the WebView URL (see
 * mobile/app/(profile)/face-verification.tsx), and this module hands back that
 * region's backend + Cognito pool. Getting it wrong is not subtle — an IN
 * Supabase token POSTed to the US backend is a flat 401, because each region's
 * backend verifies tokens against its own Supabase project.
 *
 * WHERE THE VALUES COME FROM: `window.__LIVENESS_CONFIG__`, written at container
 * start from environment variables by docker/40-write-liveness-config.sh and
 * loaded by a script tag before the bundle. `process.env` is a fallback that only
 * matters under `npm start`; the deployed image bakes in nothing.
 *
 * That indirection is the point. CRA freezes `process.env.REACT_APP_*` into the
 * bundle at build time, so configuration used to be invisible after the fact —
 * you could only learn which backend an image talked to by grepping minified JS,
 * and an image built with one region's args missing shipped looking healthy.
 *
 * Adding a region: a branch below, three variables on the service, and three
 * lines in the generator script.
 *
 * The pool IDs are public: these are *unauthenticated* Cognito identity pools,
 * designed to be reachable by anonymous browsers.
 */

export type LivenessRegion = "IN" | "US";

export type LivenessConfig = {
  /** Which region's backend + AWS account this page is operating against. */
  region: LivenessRegion;
  /** AWS region for the Cognito identity pool. Must match the region the
   *  backend creates the Rekognition session in, since the guest credentials
   *  minted here are what stream the video. */
  awsRegion?: string;
  identityPoolId?: string;
  /** Region-matched backend. Sending a token to the wrong one is a 401. */
  apiBase?: string;
};

/**
 * US is the default when the param is absent or unrecognised: it preserves the
 * behaviour of every build before this change, and an older mobile build that
 * does not append `region` is necessarily a US-only build.
 */
export function getRegionFromUrl(): LivenessRegion {
  try {
    const raw = new URL(window.location.href).searchParams.get("region");
    return raw === "IN" ? "IN" : "US";
  } catch {
    return "US";
  }
}

/** Shape written by docker/40-write-liveness-config.sh into config.js. */
type RuntimeConfig = Partial<
  Record<LivenessRegion, Partial<Omit<LivenessConfig, "region">>>
>;

function runtimeConfigFor(
  region: LivenessRegion,
): Partial<Omit<LivenessConfig, "region">> {
  const injected = (window as unknown as { __LIVENESS_CONFIG__?: RuntimeConfig })
    .__LIVENESS_CONFIG__;
  return injected?.[region] ?? {};
}

/**
 * Runtime config wins; `process.env` is the fallback so `npm start` keeps working
 * from .env. The deployed image ships no baked-in values at all — see the
 * Dockerfile — so in production the runtime path is the only one.
 *
 * Empty strings count as absent: the generator emits `""` for an unset variable,
 * and treating that as configured would defeat App.tsx's per-region guard.
 */
function pick(runtime: string | undefined, buildTime: string | undefined) {
  return runtime || buildTime || undefined;
}

export function resolveLivenessConfig(
  region: LivenessRegion = getRegionFromUrl(),
): LivenessConfig {
  const runtime = runtimeConfigFor(region);

  // Each `process.env.REACT_APP_*` reference below must stay a static literal
  // member access — CRA substitutes these textually at build time, so a
  // computed key (`process.env[`..._${region}`]`) silently resolves to
  // undefined in the built bundle.
  if (region === "IN") {
    return {
      region,
      awsRegion: pick(runtime.awsRegion, process.env.REACT_APP_AWS_REGION_IN),
      identityPoolId: pick(
        runtime.identityPoolId,
        process.env.REACT_APP_IDENTITY_POOL_ID_IN,
      ),
      apiBase: pick(runtime.apiBase, process.env.REACT_APP_API_BASE_IN),
    };
  }
  return {
    region,
    awsRegion: pick(runtime.awsRegion, process.env.REACT_APP_AWS_REGION_US),
    identityPoolId: pick(
      runtime.identityPoolId,
      process.env.REACT_APP_IDENTITY_POOL_ID_US,
    ),
    apiBase: pick(runtime.apiBase, process.env.REACT_APP_API_BASE_US),
  };
}

/**
 * Resolved once at module load. The region cannot change without a new page
 * load, since the mobile WebView builds a fresh URL per verification attempt.
 */
export const livenessConfig = resolveLivenessConfig();
